import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { PolarAngleAxis, PolarGrid, Radar, RadarChart, ResponsiveContainer } from "recharts"
import { hiabColors } from "theme"
import type { ITrainingReport, ITrainingReportParameter } from "types/trainingReportTypes"

interface Props {
  trainingReport: ITrainingReport
}

interface RadarDataPoint {
  subject: string
  A: number
  fullMark: 100
}

const normalize = (val: number, min: number, max: number): number => {
  if (val > max) val = max

  if (val < min) val = min

  return (val - min) / (max - min)
}

const addDurationDatapoint = (
  params: ITrainingReportParameter[],
  datapointArray: RadarDataPoint[],
) => {
  if (params.find((x) => x.name === "exam_duration")) {
    let val = parseFloat(params.find((x) => x.name === "exam_duration")?.value ?? "0")
    val = 1 - normalize(val, 0, 900)
    datapointArray.push({ subject: "Speed", A: val * 100, fullMark: 100 })
  }
}

const addCollisionDatapoint = (
  params: ITrainingReportParameter[],
  datapointArray: RadarDataPoint[],
) => {
  let count = 0
  let found = false

  if (params.find((x) => x.name === "examsafety_collision_tennisball")) {
    count += parseFloat(
      params.find((x) => x.name === "examsafety_collision_tennisball")?.value ?? "0",
    )
    found = true
  }
  if (params.find((x) => x.name === "examsafety_collision_pole")) {
    count += parseFloat(params.find((x) => x.name === "examsafety_collision_pole")?.value ?? "0")
    found = true
  }
  if (params.find((x) => x.name === "safety_collision_count")) {
    count += parseFloat(params.find((x) => x.name === "safety_collision_count")?.value ?? "0")
    found = true
  }

  if (found) {
    count = 1 - normalize(count, 0, 6)
    datapointArray.push({ subject: "Cautiousness", A: count * 100, fullMark: 100 })
  }
}

const addSafetyDatapoint = (
  params: ITrainingReportParameter[],
  datapointArray: RadarDataPoint[],
) => {
  let count = 0
  let found = false

  if (params.find((x) => x.name === "safety_moved_controller_active_count")) {
    count += parseFloat(
      params.find((x) => x.name === "safety_moved_controller_active_count")?.value ?? "0",
    )
    found = true
  }

  if (params.find((x) => x.name === "safety_stabilisers_grounded")) {
    const stabilisersGrounded =
      params.find((x) => x.name === "safety_stabilisers_grounded")?.value ?? "True"
    if (stabilisersGrounded !== "True") {
      count++
    }
    found = true
  }

  if (params.find((x) => x.name === "safety_user_block_stabilizer")) {
    count += parseFloat(params.find((x) => x.name === "safety_user_block_stabilizer")?.value ?? "0")
    found = true
  }

  if (params.find((x) => x.name === "safety_moved_under_boom_count")) {
    count += parseFloat(
      params.find((x) => x.name === "safety_moved_under_boom_count")?.value ?? "0",
    )
    found = true
  }

  if (found) {
    count = 1 - normalize(count, 0, 3)
    datapointArray.push({ subject: "Safety", A: count * 100, fullMark: 100 })
  }
}

const addCraneManouveringDatapoint = (
  params: ITrainingReportParameter[],
  datapointArray: RadarDataPoint[],
) => {
  if (params.find((x) => x.name === "simultaneous_lever_inputs")) {
    let val = parseFloat(params.find((x) => x.name === "simultaneous_lever_inputs")?.value ?? "0")
    val = normalize(val, 0, 2.5)
    datapointArray.push({ subject: "Manouvering", A: val * 100, fullMark: 100 })
  }
}

const addOlpCapacityDatapoint = (
  params: ITrainingReportParameter[],
  datapointArray: RadarDataPoint[],
) => {
  let boomCount = 0
  let capacity = 0
  let found = false
  let olpTriggerCount = 0

  if (params.find((x) => x.name === "olp_trigger_count")) {
    const val = parseFloat(params.find((x) => x.name === "olp_trigger_count")?.value ?? "0")
    olpTriggerCount += val
    found = true
  }

  if (params.find((x) => x.name === "olp_firstboom_capacity")) {
    const val = parseFloat(params.find((x) => x.name === "olp_firstboom_capacity")?.value ?? "0")
    capacity += val
    boomCount++
    found = true
  }

  if (params.find((x) => x.name === "olp_secondboom_capacity")) {
    const val = parseFloat(params.find((x) => x.name === "olp_secondboom_capacity")?.value ?? "0")
    capacity += val
    boomCount++
    found = true
  }

  if (params.find((x) => x.name === "olp_jibboom_capacity")) {
    const val = parseFloat(params.find((x) => x.name === "olp_jibboom_capacity")?.value ?? "0")
    capacity += val
    boomCount++
    found = true
  }

  if (found) {
    capacity = capacity / boomCount
    capacity -= olpTriggerCount * 0.5
    capacity = normalize(capacity, 0, 0.9)
    datapointArray.push({ subject: "Boom Capacity", A: capacity * 100, fullMark: 100 })
  }
}

const addLoadSwingDatapoint = (
  params: ITrainingReportParameter[],
  datapointArray: RadarDataPoint[],
) => {
  if (params.find((x) => x.name === "safety_load_swing")) {
    let val = parseFloat(params.find((x) => x.name === "safety_load_swing")?.value ?? "0")
    val = normalize(val, 0, 1)
    datapointArray.push({ subject: "Load Stability", A: val * 100, fullMark: 100 })
  }
}

const getRadarParams = (data: ITrainingReport) => {
  const radarParams: RadarDataPoint[] = []

  addDurationDatapoint(data.parameters, radarParams)
  addCollisionDatapoint(data.parameters, radarParams)
  addSafetyDatapoint(data.parameters, radarParams)
  addCraneManouveringDatapoint(data.parameters, radarParams)
  addOlpCapacityDatapoint(data.parameters, radarParams)
  addLoadSwingDatapoint(data.parameters, radarParams)

  return radarParams
}

export function TrainingResultRadarChart({ trainingReport }: Props) {
  const [chartData, setChartData] = useState<RadarDataPoint[]>([])

  useEffect(() => {
    setChartData(getRadarParams(trainingReport))
  }, [trainingReport])

  return (
    <>
      <Typography component="h4" variant="caption" color={hiabColors.greyPrimary} gutterBottom>
        (More is better)
      </Typography>
      {chartData.length >= 3 ? (
        <ResponsiveContainer width="100%" aspect={2}>
          <RadarChart cx="50%" cy="50%" outerRadius="75%" data={chartData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" />
            <Radar
              dataKey="A"
              stroke={hiabColors.redPrimary}
              fill={hiabColors.redPrimary}
              fillOpacity={0.6}
            />
          </RadarChart>
        </ResponsiveContainer>
      ) : (
        <Typography component="h5" variant="caption" color={hiabColors.blackPrimary}>
          Not enough data for display.
        </Typography>
      )}
    </>
  )
}
